import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import ImageCropper2 from 'components/ImageCropper2/ImageCropper2';
import {
  detectMediaKind,
  formatBytes,
  isUnsupportedHeic,
  MAX_FILE_SIZE,
} from 'helpers/projectMediaUpload';
import {
  uploadProjectMedia,
  discardProjectMedia,
} from 'redux/modules/textProjects';
import styles from './ProjectMediaPanel.module.scss';

// Prefer concrete types/extensions over image/* so iOS is less likely to offer HEIC.
const ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,video/*,audio/*,.mp4,.mov,.mp3,.wav,.m4a,.flac,.jpg,.jpeg,.png,.gif,.webp';

const HEIC_ERROR =
  'HEIC/HEIF photos are not supported. Please export or choose a JPEG or PNG.';

const UNSUPPORTED_FILE_ERROR =
  'Unsupported file type. Please choose an image, video, or audio file.';

/**
 * Optional media add/replace panel for project create & edit forms.
 */
const ProjectMediaPanel = ({
  mode,
  project,
  disabled,
  processing,
  onChange,
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  const [expanded, setExpanded] = useState(
    mode === 'edit' && !!project?.mediaType && project.mediaType !== 'text'
  );
  const [file, setFile] = useState(null);
  const [mediaKind, setMediaKind] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(null);
  const [cover, setCover] = useState(null);
  const [showCoverCropper, setShowCoverCropper] = useState(false);
  const [error, setError] = useState(null);
  const [removeMedia, setRemoveMedia] = useState(false);
  const [removeThumbnail, setRemoveThumbnail] = useState(false);

  const currentMediaType = project?.mediaType;
  const hasExistingMedia =
    currentMediaType && currentMediaType !== 'text' && !removeMedia;
  const previewUrl =
    project?.thumbnails?.original_optimised ||
    project?.thumbnails?.original ||
    (project?.thumbnails?.images && project.thumbnails.images[0]?.url);
  const pickerDisabled = disabled || uploading;

  const clearFileInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const emitChange = (overrides = {}) => {
    const next = {
      uploaded,
      cover,
      removeMedia,
      removeThumbnail,
      mediaKind,
      uploading,
      ...overrides,
    };

    onChange({
      file: next.uploaded?.s3_url || null,
      // Send the original File (like portfolio avatar/cover), not the cropped
      // data URL — base64 covers go through UploadLocalFilesJob and blow up
      // request size; the API crops from these coordinates.
      thumbnail: next.cover?.image || null,
      crop: next.cover
        ? {
            thumbnail: {
              x: next.cover.x,
              y: next.cover.y,
              w: next.cover.width,
              h: next.cover.height,
            },
          }
        : null,
      remove_media: next.removeMedia || false,
      remove_thumbnail: next.removeThumbnail || false,
      mediaKind: next.mediaKind,
      uploading: next.uploading,
    });
  };

  const resetLocalMedia = () => {
    setFile(null);
    setMediaKind(null);
    setProgress(0);
    setUploaded(null);
    setCover(null);
    setShowCoverCropper(false);
    setError(null);
    clearFileInput();
  };

  const handlePick = async (picked) => {
    if (!picked) return;

    // Allow re-selecting the same file after an error or clear (iOS Safari).
    clearFileInput();
    setError(null);

    if (picked.size > MAX_FILE_SIZE) {
      setError('File must be 10GB or smaller');
      return;
    }

    if (isUnsupportedHeic(picked)) {
      setError(HEIC_ERROR);
      return;
    }

    const kind = detectMediaKind(picked);
    if (kind === 'other') {
      setError(UNSUPPORTED_FILE_ERROR);
      return;
    }

    setFile(picked);
    setMediaKind(kind);
    setUploaded(null);
    setCover(null);
    setRemoveMedia(false);
    setRemoveThumbnail(false);
    setUploading(true);
    setProgress(0);

    emitChange({
      uploaded: null,
      cover: null,
      removeMedia: false,
      removeThumbnail: false,
      mediaKind: kind,
      uploading: true,
    });

    try {
      const result = await dispatch(
        uploadProjectMedia(picked, (percent) => setProgress(percent))
      );
      setUploaded(result);
      setUploading(false);
      emitChange({
        uploaded: result,
        cover: null,
        removeMedia: false,
        removeThumbnail: false,
        mediaKind: kind,
        uploading: false,
      });

      if (kind === 'audio') {
        setShowCoverCropper(true);
      }
    } catch (err) {
      setUploading(false);
      setError(err?.message || 'Upload failed. Please try again.');
      emitChange({
        uploaded: null,
        cover: null,
        removeMedia: false,
        removeThumbnail: false,
        mediaKind: kind,
        uploading: false,
      });
    }
  };

  const handleCoverDone = (details) => {
    setCover(details);
    setShowCoverCropper(false);
    emitChange({
      cover: details,
      removeMedia: false,
      removeThumbnail: false,
      uploading: false,
    });
  };

  const handleRemoveMedia = () => {
    resetLocalMedia();
    setRemoveMedia(true);
    setExpanded(true);
    emitChange({
      uploaded: null,
      cover: null,
      removeMedia: true,
      removeThumbnail: false,
      mediaKind: null,
      uploading: false,
    });
  };

  const handleRemoveCover = () => {
    setCover(null);
    setRemoveThumbnail(true);
    emitChange({
      cover: null,
      removeMedia: false,
      removeThumbnail: true,
      uploading: false,
    });
  };

  const handleClearNewMedia = async () => {
    const completedKey = uploaded?.key;

    if (completedKey) {
      try {
        await dispatch(discardProjectMedia(completedKey));
      } catch (err) {
        // best-effort cleanup of the completed queue object
      }
    }

    resetLocalMedia();
    emitChange({
      uploaded: null,
      cover: null,
      removeMedia: false,
      removeThumbnail: false,
      mediaKind: null,
      uploading: false,
    });
  };

  if (!expanded && mode === 'create') {
    return (
      <div className={styles.section}>
        <div className={styles.heading}>Featured media</div>
        <p className={styles.help}>
          Optional — attach an image thumbnail, video file, or audio file. Or
          you can create a text-only project.
        </p>
        <Button
          bsStyle="default"
          onClick={() => setExpanded(true)}
          disabled={disabled}
        >
          Add media
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.heading}>Featured media</div>
      {mode === 'create' && (
        <p className={styles.help}>
          Optional — attach an image thumbnail, video file, or audio file. Or
          you can create a text-only project.
        </p>
      )}

      {processing && (
        <div className={styles.processingBanner} role="status">
          Your media is still processing. Streams and thumbnails may not be
          ready yet. You can still edit the title, description, and roles.
          Replacing media will restart processing.
        </div>
      )}

      {hasExistingMedia && !uploaded && (
        <div className={styles.currentMedia}>
          <div>
            Current media: <strong>{currentMediaType}</strong>
            {processing ? ' (processing)' : ''}
          </div>
          {previewUrl && (
            <div className={styles.preview}>
              <img src={previewUrl} alt="Current project cover" />
            </div>
          )}
        </div>
      )}

      {removeMedia && (
        <p className={styles.help}>
          Media will be removed when you save — this project will become
          text-only.
        </p>
      )}

      {!uploaded && (
        <div
          className={styles.dropzoneWrap}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            if (pickerDisabled) return;
            handlePick(event.dataTransfer.files?.[0]);
          }}
        >
          {/*
            Native <label> association (not display:none + .click()) so iOS
            Safari opens the file picker reliably from the user gesture.
          */}
          <label
            className={`${styles.dropzone}${
              pickerDisabled ? ` ${styles.disabled}` : ''
            }`}
          >
            <p>
              {uploading
                ? 'Uploading…'
                : 'Drop a file here or click to choose (image, video, or audio)'}
            </p>
            <p className={styles.help}>Max size {formatBytes(MAX_FILE_SIZE)}</p>
            <input
              ref={inputRef}
              className={styles.fileInput}
              type="file"
              accept={ACCEPT}
              disabled={pickerDisabled}
              onChange={(event) => handlePick(event.target.files?.[0])}
            />
          </label>
        </div>
      )}

      {(file || uploading) && (
        <div className={styles.fileMeta}>
          <div>
            {file?.name} {file ? `(${formatBytes(file.size)})` : ''}
            {mediaKind ? ` · ${mediaKind}` : ''}
          </div>
          {uploading && (
            <div className={styles.progressWrap}>
              <div
                className={styles.progressBar}
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
          {uploaded && !uploading && (
            <div>
              Upload complete — ready to {mode === 'create' ? 'create' : 'save'}
              .
            </div>
          )}
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {(mediaKind === 'audio' || currentMediaType === 'audio') &&
        (showCoverCropper || cover) && (
          <div className={styles.coverSection}>
            <div className={styles.heading}>Cover image (optional)</div>
            {showCoverCropper ? (
              <ImageCropper2 aspectRatio={1} onDone={handleCoverDone} />
            ) : (
              <div className={styles.actions}>
                {cover?.croppedImage && (
                  <div className={styles.preview}>
                    <img src={cover.croppedImage} alt="Selected cover" />
                  </div>
                )}
                <Button
                  bsStyle="default"
                  bsSize="sm"
                  onClick={() => setShowCoverCropper(true)}
                  disabled={disabled}
                >
                  {cover ? 'Change cover' : 'Add cover'}
                </Button>
                {cover && (
                  <Button
                    bsStyle="link"
                    bsSize="sm"
                    onClick={() => {
                      setCover(null);
                      emitChange({
                        cover: null,
                        removeMedia: false,
                        removeThumbnail: false,
                        uploading: false,
                      });
                    }}
                  >
                    Clear new cover
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

      <div className={styles.actions}>
        {mode === 'create' && !uploaded && !uploading && (
          <Button bsStyle="link" onClick={() => setExpanded(false)}>
            Cancel
          </Button>
        )}
        {uploaded && (
          <Button
            bsStyle="link"
            onClick={handleClearNewMedia}
            disabled={disabled || uploading}
          >
            Remove new file
          </Button>
        )}
        {mode === 'edit' && hasExistingMedia && (
          <Button
            bsStyle="link"
            onClick={handleRemoveMedia}
            disabled={disabled || uploading}
          >
            Remove media (make text-only)
          </Button>
        )}
        {mode === 'edit' &&
          hasExistingMedia &&
          currentMediaType === 'audio' &&
          previewUrl &&
          !removeThumbnail && (
            <Button
              bsStyle="link"
              onClick={handleRemoveCover}
              disabled={disabled || uploading}
            >
              Remove cover
            </Button>
          )}
      </div>
    </div>
  );
};

ProjectMediaPanel.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  project: PropTypes.object,
  disabled: PropTypes.bool,
  processing: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

ProjectMediaPanel.defaultProps = {
  project: null,
  disabled: false,
  processing: false,
};

export default ProjectMediaPanel;
